import React, {useContext, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {withRouter, resolveRoute} from '@computerrock/formation-router';
import {useTranslate} from '@computerrock/formation-i18n';
import {ReactSVG} from 'react-svg';
import SidebarNavigation from './ui-elements/SidebarNavigation';
import FTEMPillarsRiveAnimation from './ui-elements/animation/FTEMPillarsRiveAnimation';
import MobileAnimation from './ui-elements/animation/MobileAnimation';
import DataTableNavigationBar from '../categories/ui-elements/DataTableNavigationBar';
import InfoCard from './ui-elements/InfoCard';
import PhaseLoader from './ui-elements/PhaseLoader';
import {MobileDevice} from '../index';
import routePaths from '../routePaths';
import {phaseTypes, subPhaseTypes} from '@swiss-ski/sski-entity-types';
import {EMPTY_ELEMENT, SUBTOPICS_ID} from '../utils/ftemConstants';
import * as dashboardActionTypes from './dashboardActionTypes';
import * as applicationActionTypes from '../application/applicationActionTypes';
import './DashboardScreen.scss';
import NewsCard from './ui-elements/NewsCard';

const DashboardScreen = ({
    subPhaseNames,
    phases,
    topics,
    detailContent,
    introductionContents,
    sports,
    history,
    match,
    initiateLinkViewModalFlow,
    initiateFetchTopicContentsFlow,
}) => {
    const {createTranslateShorthand} = useTranslate();
    const translateScreen = createTranslateShorthand('dashboard_screen');
    const {isMobile} = useContext(MobileDevice);
    const scrollRef = useRef(null);

    const [hoveredPhase, setHoveredPhase] = useState('');
    const [clickedPhase, setClickedPhase] = useState('');
    const [selectedPhase, setSelectedPhase] = useState(null);
    const [selectedSubphase, setSelectedSubphase] = useState(null);
    const [isFetching, setIsFetching] = useState(false);

    const sportId = match?.params?.sportId;
    const selectedSport = sports.find(sport => sport.id === sportId);
    const isEmpty = detailContent?.length === 1 && detailContent[0] === EMPTY_ELEMENT;

    const getFilteredDetails = () => {
        if (isEmpty) return [EMPTY_ELEMENT];
        return detailContent.filter(detail => {
            const [, , phaseName] = detail.id.split('-');
            return phaseName === clickedPhase;
        });
    };

    const sortedDetails = isEmpty
        ? getFilteredDetails()
        : SUBTOPICS_ID.filter(topic => topic !== 'introduction-home')
            .map(id => getFilteredDetails().find(detail => detail.id.includes(id)))
            .filter(Boolean);

    const scrollToTop = () => {
        scrollRef.current?.scroll({
            top: 0,
            behavior: 'smooth',
        });
    };

    const findParentPhase = phaseId => {
        let parentIndex;
        phases?.forEach((phase, index) => {
            if (phase?.subPhases?.some(topic => topic.id === phaseId)) {
                parentIndex = index;
            }
        });
        return phases[parentIndex];
    };

    useEffect(() => {
        if (!hoveredPhase || !phases.length) return;

        const phase = phases.find(p => p.id === hoveredPhase.toLowerCase());
        if (!phase) return;

        const parentPhase = findParentPhase(clickedPhase);
        const subPhase = parentPhase?.subPhases?.find(sp => sp.id === clickedPhase);

        setSelectedPhase(parentPhase || phase);
        setSelectedSubphase(subPhase || null);

        if (clickedPhase.length === 1 && clickedPhase !== 'm') {
            const hoverPillar = phases.find(p => p.id.startsWith(clickedPhase.toLowerCase()))?.id.toUpperCase();
            setHoveredPhase(hoverPillar);
            setSelectedSubphase(null);
            return;
        }

        if (parentPhase) {
            setHoveredPhase(parentPhase.id.toUpperCase());
        }

        if (subPhaseNames.includes(clickedPhase.toUpperCase())) {
            setIsFetching(true);
            initiateFetchTopicContentsFlow({
                selectedSubPhaseId: clickedPhase,
                selectedSportId: selectedSport?.idNum,
                selectedSportName: selectedSport?.id,
            });
        }
    }, [hoveredPhase, clickedPhase, phases, subPhaseNames, selectedSport, initiateFetchTopicContentsFlow]);

    useEffect(() => {
        if (sortedDetails.length > 0 || isEmpty) {
            setIsFetching(false);
        }
    }, [sortedDetails, isEmpty]);

    useEffect(() => {
        if (sortedDetails.length > 0 && isMobile) {
            scrollToTop();
        }
    }, [sortedDetails, isMobile]);

    const renderTopBar = () => (
        selectedSport && !selectedSubphase && (
            <div className="sski-c-dashboard-screen__top-bar mt-5 is-position-absolute is-flex is-justify-content-center">
                <button
                    type="button"
                    className="sski-c-dashboard-screen__button sski-c-dashboard-screen__button--small button is-size-4 is-size-6-mobile"
                    onClick={() => (isMobile
                        ? window.open(selectedSport.link?.links || 'https://snowsports.flink.host/s/Cc3DstWh/', '_blank')
                        : initiateLinkViewModalFlow({
                            linkType: 'default',
                            linkId: selectedSport.link?.id,
                        }))
                    }
                >
                    {`${translateScreen('button_label.mission')} ${selectedSport.title}`}
                    <img
                        className="sski-c-dashboard-screen__button_arrow_img ml-3"
                        alt="arrow-right"
                        src={`${process.env.PUBLIC_URL}/assets/icons/arrow-right.svg`}
                    />
                </button>
            </div>
        )
    );

    const renderAnimation = () => (isMobile ? (
        <>
            <MobileAnimation onHover={setHoveredPhase} phaseSelected={hoveredPhase.slice(0, 1)} />
            {hoveredPhase && (
            <DataTableNavigationBar
                subPhases={Object.values(subPhaseTypes).map(subPhaseType => Object.values(subPhaseType)).flat().filter(phase => phase.includes(hoveredPhase.slice(0, 1)))
                        }
                phasePrefix={hoveredPhase}
                clickedPhase={clickedPhase}
                onSelectedSubphase={setClickedPhase}
            />
            )}
        </>
    ) : (
        <>
            {!clickedPhase && <NewsCard />}
            <FTEMPillarsRiveAnimation
                subPhaseNames={subPhaseNames}
                onHover={setHoveredPhase}
                onClick={setClickedPhase}
                hoveredPhase={hoveredPhase}
                selectedPhase={selectedPhase}
                introductionContents={introductionContents}
                selectedSubphase={selectedSubphase}
                isMobile={isMobile}
                sortDetails={sortedDetails}
                setHoveredPhase={setHoveredPhase}
                clickedPhase={clickedPhase}
                setSelectedPhase={setSelectedPhase}
            />
        </>
    ));

    const renderContent = () => {
        if (!hoveredPhase || !selectedPhase) return null;

        const containerClass = `sski-c-dashboard-screen__legend-container--${selectedSubphase ? 'subphase' : hoveredPhase.toLowerCase()} ${
            clickedPhase === phaseTypes.MASTERY && !selectedSubphase
                ? 'is-flex is-justify-content-center is-align-items-center is-flex-direction-row-reverse'
                : ''
        } ${isMobile ? 'sski-c-dashboard-screen__mobile-legend-container' : ''}`;

        if (isFetching) {
            return (
                <div ref={scrollRef} className={containerClass}>
                    <div className="columns container is-desktop">
                        <PhaseLoader />
                        <PhaseLoader />
                        <PhaseLoader />
                    </div>
                </div>
            );
        }

        if (selectedSubphase && clickedPhase) {
            if (isEmpty) {
                return (
                    <div ref={scrollRef} className={containerClass}>
                        <div>No result. Please try later</div>
                    </div>
                );
            }

            if (sortedDetails.length > 0) {
                return (
                    <div ref={scrollRef} className={containerClass}>
                        {sortedDetails.map((detail, index) => (
                            <React.Fragment key={detail?.itemKey}>
                                {index === 0 && (
                                    <div className="sski-c-dashboard-screen__mobile-subphase-handler">
                                        <ReactSVG
                                            alt="close icon"
                                            src={`${process.env.PUBLIC_URL}/assets/icons/exit-circle.svg`}
                                            onClick={() => {
                                                setClickedPhase('');
                                                setSelectedSubphase(null);
                                            }}
                                        />
                                    </div>
                                )}
                                <InfoCard
                                    hoveredPhase={hoveredPhase}
                                    selectedPhase={selectedPhase}
                                    selectedSubphase={detail}
                                    topics={topics}
                                    index={index}
                                />
                            </React.Fragment>
                        ))}
                    </div>
                );
            }
        }

        if (isMobile && introductionContents && hoveredPhase && !selectedSubphase) {
            return (
                <div ref={scrollRef} className={containerClass}>
                    <InfoCard
                        hoveredPhase={hoveredPhase}
                        selectedPhase={selectedPhase}
                        selectedPhaseTitle={introductionContents[Object.values(subPhaseTypes[hoveredPhase])[0]]?.text || ''}
                        isMobile={isMobile}
                        closeCard={setHoveredPhase}
                    />
                </div>
            );
        }

        return null;
    };

    const renderSubPhaseTitle = () => clickedPhase && selectedSubphase && !isMobile && (
    <h3 className="sski-c-dashboard-screen__sub-phase-title is-size-4 has-text-weight-bold is-absolute">
        {`${clickedPhase.toUpperCase()} ${translateScreen('sub_phase_title')}`}
    </h3>
    );

    const renderInteractivePosterButton = () => ((isMobile && !hoveredPhase) || !isMobile) && (
    <div className="sski-c-dashboard-screen__button_container">
        <button
            type="button"
            className={`${isMobile ? '' : 'p-5 mr-6 mb-6'} button is-pulled-right sski-c-dashboard-screen__button`}
            onClick={() => {
                const queryParams = new URLSearchParams(history?.location.search);
                history.push(
                    resolveRoute(routePaths.CATEGORIES, {}, {search: `${queryParams.toString()}&sportId=${selectedSport?.idNum}`}),
                );
            }}
        >
            <ReactSVG alt="diagram" src={`${process.env.PUBLIC_URL}/assets/icons/diagram.svg`} />
            <p className={`ml-1 has-text-weight-medium ${isMobile ? 'is-size-5' : 'is-size-4'}`}>
                {translateScreen('button_label.interactive_poster')}
            </p>
        </button>
    </div>
    );

    return (
        <div className="grid">
            <div className="columns is-gapless">
                <div className="column is-narrow sski-c-sidebar">
                    <SidebarNavigation
                        isMobile={isMobile}
                        isSubPhase={selectedSubphase && hoveredPhase && sortedDetails.length > 0}
                        setClickedPhase={setClickedPhase}
                    />
                </div>
                <div className="column sski-c-dashboard-screen">
                    <img
                        src={`${process.env.PUBLIC_URL}/assets/sihf-ftm-bg.svg`}
                        alt="sihf-bg"
                        className="sski-c-dashboard-screen__background"
                    />
                    {renderTopBar()}
                    {renderAnimation()}
                    {renderContent()}
                    {renderSubPhaseTitle()}
                    {renderInteractivePosterButton()}
                </div>
            </div>
        </div>
    );
};

DashboardScreen.propTypes = {
    subPhaseNames: PropTypes.arrayOf(PropTypes.string),
    phases: PropTypes.arrayOf(PropTypes.object),
    introductionContents: PropTypes.object,
    topics: PropTypes.arrayOf(PropTypes.object),
    sports: PropTypes.arrayOf(PropTypes.object),
    detailContent: PropTypes.arrayOf(PropTypes.any),
    history: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired,
    initiateLinkViewModalFlow: PropTypes.func.isRequired,
    initiateFetchTopicContentsFlow: PropTypes.func.isRequired,
};

DashboardScreen.defaultProps = {
    subPhaseNames: [],
    phases: [],
    introductionContents: {},
    topics: [],
    sports: [],
    detailContent: [],
};

const mapStateToProps = state => ({
    subPhaseNames: state.dashboard.subPhaseNames,
    phases: state.dashboard.phases,
    introductionContents: state.dashboard.introductionContents,
    topics: state.dashboard.topics,
    sports: state.dashboard.sports,
    detailContent: state.dashboard.detailContent,
});

const mapDispatchToProps = dispatch => ({
    initiateLinkViewModalFlow: payload => dispatch({
        type: applicationActionTypes.OPEN_LINK_VIEW_MODAL,
        payload,
    }),
    initiateFetchTopicContentsFlow: payload => dispatch({
        type: dashboardActionTypes.INITIATE_FETCH_TOPIC_CONTENTS_FLOW,
        payload,
    }),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(DashboardScreen));
